/**
 * Generated by Kubb (https://kubb.dev/).
 * Do not edit manually.
 */

import type { RequestConfig, ResponseErrorConfig } from '../.kubb/client'
import type {
  GetMarketMakerKPIsOptions,
  GetMarketMakerKPIsStatus200,
  GetMarketMakerKPIsStatus400,
  GetMarketMakerKPIsStatus401,
  GetMarketMakerKPIsStatus403,
  GetMarketMakerKPIsStatus404,
  GetMarketMakerKPIsStatus429,
  GetMarketMakerKPIsStatus500,
} from '../types/GetMarketMakerKPIs'
import type {
  QueryKey,
  QueryClient,
  QueryObserverOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import { getMarketMakerKPIs } from '../clients/getMarketMakerKPIs'
import { queryOptions, useQuery } from '@tanstack/react-query'

export const getMarketMakerKPIsQueryKey = ({
  path,
  query,
}: Omit<GetMarketMakerKPIsOptions, 'headers'>) =>
  [
    { url: '/v1/market-makers/:market_maker_id/kpis', params: path },
    ...(query ? [query] : []),
  ] as const

type GetMarketMakerKPIsQueryKey = ReturnType<typeof getMarketMakerKPIsQueryKey>

export function getMarketMakerKPIsQueryOptions(
  { path, query }: GetMarketMakerKPIsOptions,
  config: Partial<Omit<RequestConfig, 'path' | 'query' | 'body' | 'headers' | 'url'>> = {},
) {
  const queryKey = getMarketMakerKPIsQueryKey({ path, query })
  return queryOptions<
    GetMarketMakerKPIsStatus200,
    ResponseErrorConfig<
      | GetMarketMakerKPIsStatus400
      | GetMarketMakerKPIsStatus401
      | GetMarketMakerKPIsStatus403
      | GetMarketMakerKPIsStatus404
      | GetMarketMakerKPIsStatus429
      | GetMarketMakerKPIsStatus500
    >,
    GetMarketMakerKPIsStatus200,
    typeof queryKey
  >({
    queryKey,
    queryFn: async ({ signal }) => {
      return getMarketMakerKPIs({
        ...config,
        path,
        query,
        signal: config.signal ?? signal,
        throwOnError: true,
      }).unwrap()
    },
  })
}

/**
 * @description Admins and the market maker's own user. Evaluates the UTC day or month containing `at` (default now) against the daily or monthly goals. `coverage` tells how far the collector has finalized the window. `assigned` is the current assignment while unauthorized volume reflects the assignment at finalization. Depth values are the average over the window of each minute's minimum depth; /metrics reports the minimum within each bucket. meets_goal is null when undecidable: no known minutes, no goal, uptime/spread/depth while any minute of the window is not finalized, unknown, partly observed or incomplete, or a volume below goal while the window is not fully finalized, not measured from its start, or contains minutes a late event changed after they were stored (lower_bound). Without a replenishment goal, replenishment counts are reported but nothing is judged: within_goal_ratio is null.
 * @summary Market maker KPIs against goals
 * {@link /v1/market-makers/:market_maker_id/kpis}
 */
export function useGetMarketMakerKPIs<
  TData = GetMarketMakerKPIsStatus200,
  TQueryData = GetMarketMakerKPIsStatus200,
  TQueryKey extends QueryKey = GetMarketMakerKPIsQueryKey,
>(
  {
    path,
    query,
  }: {
    path: GetMarketMakerKPIsOptions['path'] | (() => GetMarketMakerKPIsOptions['path'])
    query?: GetMarketMakerKPIsOptions['query'] | (() => GetMarketMakerKPIsOptions['query'])
  },
  options: {
    query?: Partial<
      QueryObserverOptions<
        GetMarketMakerKPIsStatus200,
        ResponseErrorConfig<
          | GetMarketMakerKPIsStatus400
          | GetMarketMakerKPIsStatus401
          | GetMarketMakerKPIsStatus403
          | GetMarketMakerKPIsStatus404
          | GetMarketMakerKPIsStatus429
          | GetMarketMakerKPIsStatus500
        >,
        TData,
        TQueryData,
        TQueryKey
      >
    > & { client?: QueryClient }
    client?: Partial<Omit<RequestConfig, 'path' | 'query' | 'body' | 'headers' | 'url'>>
  } = {},
) {
  const { query: queryConfig = {}, client: config = {} } = options ?? {}
  const { client: queryClient, ...resolvedOptions } = queryConfig
  const resolvedParams = {
    path: typeof path === 'function' ? path() : path,
    query: typeof query === 'function' ? query() : query,
  }
  const queryKey = resolvedOptions?.queryKey ?? getMarketMakerKPIsQueryKey(resolvedParams)

  const queryResult = useQuery(
    {
      ...getMarketMakerKPIsQueryOptions(resolvedParams, config),
      ...resolvedOptions,
      queryKey,
    } as unknown as QueryObserverOptions,
    queryClient,
  ) as UseQueryResult<
    TData,
    ResponseErrorConfig<
      | GetMarketMakerKPIsStatus400
      | GetMarketMakerKPIsStatus401
      | GetMarketMakerKPIsStatus403
      | GetMarketMakerKPIsStatus404
      | GetMarketMakerKPIsStatus429
      | GetMarketMakerKPIsStatus500
    >
  > & { queryKey: TQueryKey }

  queryResult.queryKey = queryKey as TQueryKey

  return queryResult
}
